import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import TurnstileWidget from "@/components/TurnstileWidget";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/auth/forgot-password", { email, turnstile_token: turnstileToken });
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="forgot-password-page">
      <p className="overline text-center">Reset access</p>
      <h1 className="font-display text-4xl text-center mt-3">Forgot your password?</h1>
      {sent ? (
        <div className="mt-10 space-y-4" data-testid="forgot-sent">
          <p className="text-center text-[var(--ff-text-secondary)]">
            If an account exists for <strong>{email}</strong>, we've sent a reset link. Check your inbox.
          </p>
          <div
            className="rounded-2xl border border-[var(--ff-border)] bg-[var(--ff-cream)] p-5 text-sm text-[var(--ff-text-secondary)]"
            data-testid="forgot-google-hint"
          >
            <p className="font-medium text-[var(--ff-text-primary)]">Signed up with Google?</p>
            <p className="mt-2">
              If you originally registered using <em>Continue with Google</em>, your account doesn't have a password to reset — you won't receive a reset email.
              Just head to the{" "}
              <Link to="/login" className="underline text-[var(--ff-primary)]">
                sign-in page
              </Link>{" "}
              and click <strong>Continue with Google</strong>.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-4">
          <input className="input-base" placeholder="Email" type="email" value={email}
            onChange={e => setEmail(e.target.value)} required data-testid="forgot-email"/>
          <TurnstileWidget onVerify={setTurnstileToken}/>
          {err && <p className="text-sm text-red-700">{err}</p>}
          <button type="submit" className="btn-primary w-full justify-center" data-testid="forgot-submit">
            Send reset link
          </button>
        </form>
      )}
      <p className="mt-8 text-center text-sm">
        <Link to="/login" className="underline text-[var(--ff-primary)]">Back to sign in</Link>
      </p>
    </div>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const next = sp.get("next");
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) { setErr("Passwords don't match"); return; }
    setErr("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pwd });
      if (data?.access_token) localStorage.setItem("ff2d_token", data.access_token);
      setOk(true);
      // Full reload so AuthContext picks up the new session, then land on the
      // requested destination (e.g. the ambassador dashboard) or sign-in.
      const dest = next || (data?.access_token ? "/" : "/login");
      setTimeout(() => { window.location.href = dest; }, 1200);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!token) return <div className="p-10 text-center" data-testid="reset-no-token">Invalid reset link.</div>;

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="reset-password-page">
      <h1 className="font-display text-4xl text-center">Choose a new password</h1>
      {ok ? (
        <p className="mt-10 text-center text-[var(--ff-primary)]" data-testid="reset-success">Password updated. Redirecting…</p>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-4">
          <input className="input-base" placeholder="New password (6+ chars)" type="password" minLength={6}
            value={pwd} onChange={e => setPwd(e.target.value)} required data-testid="reset-pwd"/>
          <input className="input-base" placeholder="Confirm" type="password" minLength={6}
            value={confirm} onChange={e => setConfirm(e.target.value)} required data-testid="reset-confirm"/>
          {err && <p className="text-sm text-red-700">{err}</p>}
          <button type="submit" className="btn-primary w-full justify-center" data-testid="reset-submit">
            Update password
          </button>
        </form>
      )}
    </div>
  );
}

function ReferralShare() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/referrals/me"); setData(data); }
    catch {}
  };
  if (data === null) { load(); }
  if (!data) return null;
  const inviteUrl = `${window.location.origin}${data.invite_url_suffix}`;
  const copy = () => { navigator.clipboard?.writeText(inviteUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="mt-10 card-flat p-6 bg-[var(--ff-muted)]" data-testid="verify-share-card">
      <p className="overline text-[var(--ff-secondary)]">Give $10, get $10</p>
      <h3 className="font-display text-2xl mt-1">Share your code, give your friends $10, get $10 back.</h3>
      <p className="text-sm text-[var(--ff-text-secondary)] mt-2">
        Send a friend your unique link. They get $10 off their first month, you get $10 credit on your next renewal.
      </p>
      <div className="mt-4 flex gap-2">
        <input readOnly value={inviteUrl} className="input-base flex-1 text-sm" data-testid="verify-share-url"/>
        <button onClick={copy} className="btn-primary" data-testid="verify-share-copy">
          {copied ? "Copied!" : "Copy link"}
        </button>
      </div>
    </div>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const [state, setState] = useState("pending");
  const [err, setErr] = useState("");

  if (token && state === "pending") {
    api.post("/auth/verify-email", { token })
      .then(() => setState("ok"))
      .catch(e => { setState("error"); setErr(formatApiError(e.response?.data?.detail)); });
  }

  return (
    <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="verify-email-page">
      {state === "pending" && <p>Verifying your email…</p>}
      {state === "ok" && (
        <>
          <h1 className="font-display text-3xl text-[var(--ff-primary)]" data-testid="verify-ok">Email verified ✓</h1>
          <p className="mt-3 text-[var(--ff-text-secondary)]">You can now access all member features.</p>
          <Link to="/marketplace" className="btn-primary mt-8 inline-flex">Shop the marketplace</Link>
          <ReferralShare/>
        </>
      )}
      {state === "error" && (
        <>
          <h1 className="font-display text-3xl">Verification failed</h1>
          <p className="mt-3 text-red-700" data-testid="verify-err">{err}</p>
        </>
      )}
    </div>
  );
}
